import React from 'react';
import {
  PlaceDetails,
  formatPlaceType,
  getOpenStatus,
  priceLevelLabels,
} from './placeDetails';
import './MapPlaceSidebar.css';

interface PlaceDetailsPanelProps {
  place: PlaceDetails | null;
  isLoading: boolean;
  emptyTitle?: string;
  emptyMessage?: string;
}

interface DetailRowProps {
  icon: string;
  label: string;
  value: string;
  href?: string;
  external?: boolean;
}

const DetailRow: React.FC<DetailRowProps> = ({ icon, label, value, href, external = false }) => (
  <div className="v2-place-card__detail-row">
    <span aria-hidden>{icon}</span>
    <div>
      <strong>{label}</strong>
      {href ? (
        <a href={href} target={external ? '_blank' : undefined} rel={external ? 'noreferrer' : undefined}>
          {value}
        </a>
      ) : (
        <p>{value}</p>
      )}
    </div>
  </div>
);

const PlaceDetailsPanel: React.FC<PlaceDetailsPanelProps> = ({
  place,
  isLoading,
  emptyTitle = 'No place selected',
  emptyMessage = 'Tap a pin on the map or select an activity to see photos, hours, and details.',
}) => {
  const [showAllHours, setShowAllHours] = React.useState(false);

  React.useEffect(() => {
    setShowAllHours(false);
  }, [place?.name]);

  if (isLoading) {
    return (
      <section className="v2-place-card" aria-label="Place details">
        <div className="v2-place-card__state">
          <span className="v2-place-card__spinner" />
          <p>Loading place details...</p>
        </div>
      </section>
    );
  }

  if (!place) {
    return (
      <section className="v2-place-card" aria-label="Place details">
        <div className="v2-place-card__empty">
          <span className="v2-place-card__pin" aria-hidden>📍</span>
          <h2>{emptyTitle}</h2>
          <p>{emptyMessage}</p>
        </div>
      </section>
    );
  }

  const openStatus = getOpenStatus(place);
  const phone = place.formatted_phone_number || place.international_phone_number;
  const photos = place.photos?.slice(0, 8) || [];
  const heroPhoto = photos[0];
  const thumbPhotos = photos.slice(1);
  const displayTypes = (place.types || [])
    .filter((type) => !['point_of_interest', 'establishment', 'geocode'].includes(type))
    .slice(0, 4);

  return (
    <section className="v2-place-card" aria-label="Place details">
      {heroPhoto && (
        <div className="v2-place-card__photos">
          <img
            src={heroPhoto.getUrl({ maxWidth: 900, maxHeight: 480 })}
            alt=""
            className="v2-place-card__hero"
          />
          {thumbPhotos.length > 0 && (
            <div className="v2-place-card__thumbs">
              {thumbPhotos.map((photo, index) => (
                <img
                  key={`${place.name}-photo-${index + 1}`}
                  src={photo.getUrl({ maxWidth: 240, maxHeight: 160 })}
                  alt=""
                />
              ))}
            </div>
          )}
        </div>
      )}

      <div className="v2-place-card__body">
        <div className="v2-place-card__head">
          <h2>{place.name}</h2>
          {place.rating !== undefined && (
            <div className="v2-place-card__rating">
              <span className="v2-place-card__stars" aria-hidden>
                {'★'.repeat(Math.round(place.rating))}
                {'☆'.repeat(Math.max(0, 5 - Math.round(place.rating)))}
              </span>
              <strong>{place.rating.toFixed(1)}</strong>
              {place.user_ratings_total ? <span>({place.user_ratings_total.toLocaleString()} reviews)</span> : null}
            </div>
          )}
        </div>

        {displayTypes.length > 0 && (
          <div className="v2-place-card__types">
            {displayTypes.map((type) => (
              <span key={type}>{formatPlaceType(type)}</span>
            ))}
            {place.price_level !== undefined && place.price_level > 0 && (
              <span>{priceLevelLabels[place.price_level] || '$'}</span>
            )}
          </div>
        )}

        {place.editorial_summary?.overview && (
          <p className="v2-place-card__summary">{place.editorial_summary.overview}</p>
        )}

        <div className="v2-place-card__details">
          {place.formatted_address && (
            <DetailRow icon="📍" label="Address" value={place.formatted_address} />
          )}

          {place.opening_hours && (
            <div className="v2-place-card__hours-block">
              <div className="v2-place-card__hours-head">
                <span aria-hidden>🕐</span>
                <div>
                  <strong>Hours</strong>
                  {openStatus !== null && (
                    <span className={`v2-place-card__open-status ${openStatus ? 'v2-place-card__open-status--open' : 'v2-place-card__open-status--closed'}`}>
                      {openStatus ? 'Open now' : 'Closed now'}
                    </span>
                  )}
                </div>
              </div>
              {place.opening_hours.weekday_text && (
                <div className="v2-place-card__hours-list">
                  {(showAllHours ? place.opening_hours.weekday_text : place.opening_hours.weekday_text.slice(0, 1)).map((row) => (
                    <p key={row}>{row}</p>
                  ))}
                  {place.opening_hours.weekday_text.length > 1 && (
                    <button type="button" onClick={() => setShowAllHours((current) => !current)}>
                      {showAllHours ? 'Show less' : 'Show all hours'}
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {phone && (
            <DetailRow icon="📞" label="Phone" value={phone} href={`tel:${phone.replace(/\s/g, '')}`} />
          )}

          {place.website && (
            <DetailRow icon="🌐" label="Website" value="Visit website" href={place.website} external />
          )}

          {place.url && (
            <DetailRow icon="🗺️" label="Google Maps" value="View on Google Maps" href={place.url} external />
          )}

          {place.business_status && place.business_status !== 'OPERATIONAL' && (
            <DetailRow icon="ℹ️" label="Status" value={formatPlaceType(place.business_status.toLowerCase())} />
          )}
        </div>
      </div>
    </section>
  );
};

export default PlaceDetailsPanel;
